import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setAuthToken, getStoredToken } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    const token = getStoredToken();
    if (token) {
      setAuthToken(token);
      loadMe().finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [loadMe]);

  // the assistant greets each new login, and a new login never reopens the previous person's chat
  const freshAssistant = () => {
    try { sessionStorage.removeItem("buildnexa.assistant.hello"); sessionStorage.removeItem("buildnexa.assistant.chat"); } catch { /* private window */ }
  };

  /** Finishes a sign-in once the server has given a real session token. */
  const finishLogin = async (accessToken) => {
    setAuthToken(accessToken);
    freshAssistant();
    return loadMe();
  };

  /** Returns the person when signed in; or { step: "verify" | "enroll", mfaToken } when a second step is needed first. */
  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.mfa_required || data.mfa_enroll_required) return { step: data.mfa_required ? "verify" : "enroll", mfaToken: data.mfa_token, method: data.mfa_method || "APP", sentTo: data.mfa_sent_to || null };
    return finishLogin(data.access_token);
  };

  const signup = async (payload) => {
    const { data } = await api.post("/auth/signup", payload);
    setAuthToken(data.access_token);
    freshAssistant();
    return loadMe();
  };

  const logout = () => {
    freshAssistant();
    setAuthToken(null);
    setUser(null);
    window.location.href = "/login";
  };

  const isOrgAdmin = user?.role === "ORG_ADMIN";
  const seesWholePortfolio = user?.role === "ORG_ADMIN" || user?.role === "PORTFOLIO_LEAD";

  return (
    <AuthContext.Provider
      value={{ user, loading, login, finishLogin, signup, logout, isOrgAdmin, seesWholePortfolio, refreshMe: loadMe }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
